import * as yup from 'yup';

/**
 * Helper to safely convert empty string to undefined
 * (prevents NaN issues from MUI inputs)
 */
const numberField = (min = 0, max = 100, required = false) => {
  let schema = yup
    .number()
    .transform((value, originalValue) => (originalValue === '' ? undefined : value))
    .typeError('Must be a number')
    .min(min, `Minimum ${min}`)
    .max(max, `Maximum ${max}`);

  if (required) {
    schema = schema.required('Required');
  }

  return schema;
};

export const createPayrollPolicySchema = yup
  .object()
  .shape({
    // =========================
    // BASIC
    // =========================
    name: yup
      .string()
      .min(3, 'Policy name must be at least 3 characters')
      .required('Policy name is required'),

    // =========================
    // SALARY STRUCTURE
    // =========================
    basicPercent: numberField(0, 100, true),
    hraPercent: numberField(0, 100, true),
    specialAllowancePercent: numberField(0, 100, true),

    // =========================
    // PROFESSIONAL TAX
    // =========================
    professionalTaxEnabled: yup.boolean(),
    professionalTaxAmount: yup
      .number()
      .min(1)
      .transform((v, o) => (o === '' ? undefined : v))
      .when('professionalTaxEnabled', {
        is: true,
        then: (schema) => schema.required('Professional tax amount required'),
        otherwise: (schema) => schema.notRequired(),
      }),

    // =========================
    // PF
    // =========================
    pfEnabled: yup.boolean(),
    pfEmployeePercent: numberField(0, 100).when('pfEnabled', {
      is: true,
      then: (schema) => schema.required('Employee PF % required'),
      otherwise: (schema) => schema.notRequired(),
    }),
    pfEmployerPercent: numberField(0, 100).when('pfEnabled', {
      is: true,
      then: (schema) => schema.required('Employer PF % required'),
      otherwise: (schema) => schema.notRequired(),
    }),

    // =========================
    // ESIC
    // =========================
    esicEnabled: yup.boolean(),
    esicEmployeePercent: numberField(0, 100).when('esicEnabled', {
      is: true,
      then: (schema) => schema.required('Employee ESIC % required'),
      otherwise: (schema) => schema.notRequired(),
    }),
    esicEmployerPercent: numberField(0, 100).when('esicEnabled', {
      is: true,
      then: (schema) => schema.required('Employer ESIC % required'),
      otherwise: (schema) => schema.notRequired(),
    }),

    // =========================
    // LEAVES
    // =========================
    slPerYear: yup
      .number()
      .transform((v, o) => (o === '' ? undefined : v))
      .min(1, 'Minimum 1')
      .required('Sick leave required'),

    plPerYear: yup
      .number()
      .transform((v, o) => (o === '' ? undefined : v))
      .min(1, 'Minimum 1')
      .required('Paid leave required'),

    plCarryForward: yup.boolean(),
    maxPlCarryForward: yup
      .number()
      .transform((v, o) => (o === '' ? undefined : v))
      .when('plCarryForward', {
        is: true,
        then: (schema) =>
          schema.required('Max PL carry forward required').min(0, 'Cannot be negative'),
        otherwise: (schema) => schema.notRequired(),
      }),

    // =========================
    // DATES
    // =========================
    effectiveFrom: yup
      .date()
      .typeError('Effective From is required')
      .required('Effective From is required'),

    effectiveTo: yup
      .date()
      .nullable()
      .transform((v, o) => (o === '' ? null : v))
      .min(yup.ref('effectiveFrom'), 'Must be after Effective From'),

    isActive: yup.boolean(),
  })
  .test('salary-total-check', null, function (values) {
    const { basicPercent, hraPercent, specialAllowancePercent } = values;

    if (basicPercent == null || hraPercent == null || specialAllowancePercent == null) return true;

    const hraOfBasic = (basicPercent * hraPercent) / 100;
    const total = basicPercent + hraOfBasic + specialAllowancePercent;

    if (total > 100) {
      return this.createError({
        path: 'salaryStructureError', // 👈 custom virtual field
        message: 'Basic + HRA (of Basic) + Special Allowance cannot exceed 100%',
      });
    }
    if (total < 100) {
      return this.createError({
        path: 'salaryStructureError', // 👈 custom virtual field
        message: 'Basic + HRA (of Basic) + Special Allowance cannot less than 100%',
      });
    }

    return true;
  });
